import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import { v4 as uuidv4 } from "uuid";
import { plainToClass } from "class-transformer";
import { mergeWith } from "lodash";
import { toJS } from "mobx";

const SESSION_STORAGE_CHARGE_ORDERS_KEY = "charge_orders";
const SESSION_STORAGE_CHARGE_ORDER_STATUS_KEY = "charge_order_status";

function arrayMergeCustomizer(objValue: any, srcValue: any) {
  // Když máme pole objektů s .id, mergneme je podle id
  if (
    Array.isArray(objValue) &&
    Array.isArray(srcValue) &&
    objValue.every((o: any) => o && o.id != null) &&
    srcValue.every((o: any) => o && o.id != null)
  ) {
    const result: any[] = [];
    // 1) vezmi všechny staré, mergej pokud existuje nový
    objValue.forEach(oldItem => {
      const newItem = srcValue.find((o: any) => o.id === oldItem.id);
      result.push(newItem ? mergeWith({}, oldItem, newItem, arrayMergeCustomizer) : oldItem);
    });
    // 2) přidej zcela nové položky
    srcValue.forEach(newItem => {
      if (!result.find(o => o.id === newItem.id)) {
        result.push(newItem);
      }
    });
    return result;
  }
  // Jinak nech lodash.mergeWith dělat default (většinou srcValue přepíše)
}
export default class ChargeOrderStorageRepository {
  getLastChargeOrder(): ChargeOrder | undefined {
    return this.getChargeOrders()
      .sort(
        (chargeOrder1, chargeOrder2) => new Date(chargeOrder2._createdAt).getTime() - new Date(chargeOrder1._createdAt).getTime()
      )
      .shift();
  }

  getChargeOrder(id: string): ChargeOrder | undefined {
    return this.getChargeOrders().find(o => o.id === id);
  }

  addChargeOrder(chargeOrder: ChargeOrder) {
    if (!chargeOrder.id) {
      chargeOrder.id = uuidv4();
      chargeOrder._createdAt = new Date();
    }

    let chargeOrders: ChargeOrder[] = [];
    const ordersString = sessionStorage.getItem(SESSION_STORAGE_CHARGE_ORDERS_KEY);
    if (ordersString) {
      chargeOrders = JSON.parse(ordersString) as ChargeOrder[];
    }
    chargeOrders.push(chargeOrder);

    sessionStorage.setItem(SESSION_STORAGE_CHARGE_ORDERS_KEY, JSON.stringify(chargeOrders));
  }

  updateChargeOrder(chargeOrder: ChargeOrder) {
    // 1) převedeme MobX instanci na čistý JS
    const plainNew = toJS(chargeOrder);
    // 2) načteme storage
    const raw = sessionStorage.getItem(SESSION_STORAGE_CHARGE_ORDERS_KEY);
    const arr: any[] = raw ? JSON.parse(raw) : [];

    // 3) najdeme index
    const idx = arr.findIndex(o => o.id === plainNew.id);
    if (idx !== -1) {
      // 4a) deep-merge starého a nového včetně polí ‚charges‘
      arr[idx] = mergeWith({}, arr[idx], plainNew, arrayMergeCustomizer);
    } else {
      // 4b) nový záznam
      if (!plainNew.id) {
        plainNew.id = uuidv4();
        plainNew._createdAt = new Date();
      }
      arr.push(plainNew);
    }

    // 5) uložíme zpátky
    sessionStorage.setItem(SESSION_STORAGE_CHARGE_ORDERS_KEY, JSON.stringify(arr));
  }

  getChargeOrders() {
    const raw = sessionStorage.getItem(SESSION_STORAGE_CHARGE_ORDERS_KEY);
    const arr = raw ? (JSON.parse(raw) as ChargeOrder[]) : [];
    return arr.map((o: any) => plainToClass(ChargeOrder, o));
  }

  removeChargeOrder(id: string) {
    let chargeOrders = this.getChargeOrders();

    chargeOrders = chargeOrders.filter(chargeOrder => chargeOrder.id !== id);

    this.storeChargeOrders(chargeOrders);
  }

  storeChargeOrders(chargeOrders: ChargeOrder[]) {
    sessionStorage.setItem(SESSION_STORAGE_CHARGE_ORDERS_KEY, JSON.stringify(chargeOrders));
  }

  clearChargeOrders() {
    sessionStorage.removeItem(SESSION_STORAGE_CHARGE_ORDERS_KEY);
  }

  storeChargeStatus(status: ChargeOrderStatus) {
    sessionStorage.setItem(SESSION_STORAGE_CHARGE_ORDER_STATUS_KEY, JSON.stringify(status));
  }

  clearChargeStatus() {
    sessionStorage.removeItem(SESSION_STORAGE_CHARGE_ORDER_STATUS_KEY);
  }

  getChargeStatus() {
    sessionStorage.getItem(SESSION_STORAGE_CHARGE_ORDER_STATUS_KEY);
  }
}
